import SectionOne from '@/features/section-1/application/domain/entities/SectionOne';
import { SectionOneForm } from '@/features/section-1/application/validators/section-one.schema';
import Section2 from '@/features/section-2/application/domain/entities/Section2';
import { useFormattedDocumentTitle } from '@/shared/infra/hooks/useFormattedDocumentTitle';
import { ListProps, Section2ToSection2PageMapper } from '../utils/utils';
import { fitDocumentTitle } from '../utils/fit-document-title';
import { additionalInformationStyle, fillTextStyle, signatureNoteStyle } from './fill-styles';

/**
 * Section 2's values on page 1 of the generated I-9. Both generators render this one component (the
 * record viewer and the Section 1 review copy), so what Section 2 prints cannot differ between them.
 *
 * Positions are in the page's PDF points, measured from each printed label: the three Document rows
 * of List A sit 72pt apart, and the Additional Information box starts right of List A's Document 2.
 */
const LIST_X_POSITION_DELTA = 150;
const LIST_A_X_POSITION = 0;
const LIST_B_X_POSITION = LIST_X_POSITION_DELTA;
const LIST_C_X_POSITION = LIST_X_POSITION_DELTA * 2;
const Y_POSITION_DELTA = 18;
/** List A's Document 2 and Document 3 title rows. */
const LIST_A_ROW_Y = [74, 146];
const ADDITIONAL_INFORMATION_X = 140;
const ADDITIONAL_INFORMATION_Y = 82;
const ADDITIONAL_INFORMATION_LINE_HEIGHT = 9;
const EMPLOYER_DETAILS_X_POSITION = -90;
const EMPLOYER_DETAILS_Y_POSITION = 260;
const EMPLOYER_DETAILS_Y_POSITION_DELTA = 30;

type TitleLines = ReturnType<typeof fitDocumentTitle>;

function DocumentRow({
  x,
  y,
  title,
  titleStyle,
  row,
}: {
  x: number;
  y: number;
  title: TitleLines;
  titleStyle: typeof fillTextStyle;
  row: ListProps;
}) {
  const shift = title.line2 ? 8 : 0;
  return (
    <>
      <tspan x={x} y={y} style={titleStyle}>
        {title.line1}
      </tspan>
      {title.line2 ? (
        <tspan x={x} y={y + 8} style={titleStyle}>
          {title.line2}
        </tspan>
      ) : null}
      <tspan x={x} y={y + Y_POSITION_DELTA + shift} style={fillTextStyle}>
        {row.issueAuthority}
      </tspan>
      <tspan x={x} y={y + Y_POSITION_DELTA * 2 + shift} style={fillTextStyle}>
        {row.docNum}
      </tspan>
      <tspan x={x} y={y + Y_POSITION_DELTA * 3 + shift} style={fillTextStyle}>
        {row.expDate}
      </tspan>
    </>
  );
}

export default function Section2Fill({
  section1,
  section2,
}: {
  section1: SectionOne;
  section2?: Section2;
}) {
  const section2Data = Section2ToSection2PageMapper(
    section2,
    section1.formFields as SectionOneForm,
  );

  // A row that is one document of a combination prints that document's own title; otherwise the
  // key's title. Each is fitted to its cell by measured width (VTK-1896).
  const formattedA = useFormattedDocumentTitle(section2Data?.docAListKey);
  const docATitleLines = fitDocumentTitle(section2Data?.A?.title ?? formattedA);
  // A served row (a receipt: "Receipt: " and the document it replaces) prints its own title.
  const formattedB = useFormattedDocumentTitle(section2Data?.docBListKey);
  const formattedC = useFormattedDocumentTitle(section2Data?.docCListKey);
  const docBTitleLines = fitDocumentTitle(section2Data?.B?.title ?? formattedB);
  const docCTitleLines = fitDocumentTitle(section2Data?.C?.title ?? formattedC);
  const styleFor = (lines: TitleLines) => ({ ...fillTextStyle, fontSize: `${lines.fontSize}px` });

  if (section2Data === null) return null;

  // Conditional Y positions based on whether title is multi-line
  const LIST_A_Y_POSITION = docATitleLines.line2 ? -2 : 2;
  const LIST_B_Y_POSITION = docBTitleLines.line2 ? -3 : 0;
  const LIST_C_Y_POSITION = docCTitleLines.line2 ? -2 : 2;

  return (
    <text
      xmlSpace="preserve"
      transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 130 350)"
      fontVariant="normal"
      fontWeight={400}
      fontStretch="normal"
      fontSize="7px"
      fontFamily="Arial"
      writingMode="lr-tb"
      fill="#231f20"
      fillOpacity={1}
      fillRule="nonzero"
      stroke="none"
    >
      {section2Data.A && (
        <DocumentRow
          x={LIST_A_X_POSITION}
          y={LIST_A_Y_POSITION}
          title={docATitleLines}
          titleStyle={styleFor(docATitleLines)}
          row={section2Data.A}
        />
      )}
      {[section2Data.A2, section2Data.A3].map((row, i) => {
        if (!row) return null;
        const title = fitDocumentTitle(row.title ?? '');
        return (
          <DocumentRow
            key={i}
            x={LIST_A_X_POSITION}
            y={LIST_A_ROW_Y[i] - (title.line2 ? 4 : 0)}
            title={title}
            titleStyle={styleFor(title)}
            row={row}
          />
        );
      })}
      {section2Data.B && (
        <DocumentRow
          x={LIST_B_X_POSITION}
          y={LIST_B_Y_POSITION}
          title={docBTitleLines}
          titleStyle={styleFor(docBTitleLines)}
          row={section2Data.B}
        />
      )}
      {section2Data.C && (
        <DocumentRow
          x={LIST_C_X_POSITION}
          y={LIST_C_Y_POSITION}
          title={docCTitleLines}
          titleStyle={styleFor(docCTitleLines)}
          row={section2Data.C}
        />
      )}

      {section2Data.additionalInformationLines.map((line, i) => (
        <tspan
          key={`info-${i}`}
          x={ADDITIONAL_INFORMATION_X}
          y={ADDITIONAL_INFORMATION_Y + ADDITIONAL_INFORMATION_LINE_HEIGHT * i}
          style={additionalInformationStyle}
          data-testid="additional-information-line"
        >
          {line}
        </tspan>
      ))}

      <tspan x={330} y={235} style={fillTextStyle}>
        {section2Data.firstDateOfEmployment}
      </tspan>

      <tspan x={EMPLOYER_DETAILS_X_POSITION} y={EMPLOYER_DETAILS_Y_POSITION} style={fillTextStyle}>
        {section2Data.employerNameAndRole}
      </tspan>
      <tspan
        x={EMPLOYER_DETAILS_X_POSITION + 260}
        y={EMPLOYER_DETAILS_Y_POSITION}
        style={fillTextStyle}
      >
        {section2Data.employerSignature}
      </tspan>
      {section2Data.employerSignature ? (
        <tspan
          x={EMPLOYER_DETAILS_X_POSITION + 260}
          y={EMPLOYER_DETAILS_Y_POSITION + 10}
          style={signatureNoteStyle}
        >
          {section2Data.signatureDate
            ? `Electronically signed on ${section2Data.signatureDate}`
            : 'Electronically signed'}
        </tspan>
      ) : null}
      <tspan
        x={EMPLOYER_DETAILS_X_POSITION + 450}
        y={EMPLOYER_DETAILS_Y_POSITION}
        style={fillTextStyle}
      >
        {section2Data.signatureDate}
      </tspan>
      <tspan
        x={EMPLOYER_DETAILS_X_POSITION}
        y={EMPLOYER_DETAILS_Y_POSITION + EMPLOYER_DETAILS_Y_POSITION_DELTA}
        style={fillTextStyle}
      >
        {section2Data.accountName}
      </tspan>
      <tspan
        x={EMPLOYER_DETAILS_X_POSITION + 210}
        y={EMPLOYER_DETAILS_Y_POSITION + EMPLOYER_DETAILS_Y_POSITION_DELTA}
        style={fillTextStyle}
      >
        {section2Data.locationAddress}
      </tspan>
    </text>
  );
}
